import { SearchableText } from "../../../../../shared/components/search/Searchable"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useDateTime } from "../../../../localization/useDateTime"

import { formatTimeWindow } from "../meetingRoomsLib"
import type { MeetingRoomBookingItem, MeetingRoomHourGroup, MeetingRoomRow } from "../meetingRoomsTypes"
import { MeetingRoomBookingRow } from "./MeetingRoomBookingRow"
import { MeetingRoomFreeRow } from "./MeetingRoomFreeRow"
import { MeetingRoomsGroup } from "./MeetingRoomsGroup"
import { MeetingRoomsGroupsSkeleton } from "./MeetingRoomsGroupsSkeleton"

type Props = Readonly<{
    isLoading: boolean
    isFreeRoomsShown: boolean
    groups: ReadonlyArray<MeetingRoomHourGroup>
    totalRooms: number
    currentHour?: number
    searchRegex?: RegExp
    onOpen: (item: MeetingRoomBookingItem) => void
    onRoomOpen: (room: MeetingRoomRow) => void
}>

export function MeetingRoomsHourGroups({
    isLoading,
    isFreeRoomsShown,
    groups,
    totalRooms,
    currentHour,
    searchRegex,
    onOpen,
    onRoomOpen,
}: Props) {
    const dateTimeFactory = useDateTime()

    if (isLoading) {
        return <MeetingRoomsGroupsSkeleton />
    }

    return (
        <>
            {groups.map(({ hour, timeRange, items, freeRooms }) => {
                const count = isFreeRoomsShown ? freeRooms.length : items.length
                const hourLabel = formatTimeWindow(dateTimeFactory, timeRange)

                return (
                    <MeetingRoomsGroup
                        key={hour}
                        label={hourLabel}
                        meta={
                            <LocalizedStrict
                                id="service-center-meeting-rooms-group-hour-meta"
                                vars={{ count, total: totalRooms }}
                            >
                                <span>
                                    {count} of {totalRooms} rooms
                                </span>
                            </LocalizedStrict>
                        }
                        isHighlighted={hour === currentHour}
                        isEmpty={!count}
                        emptyText={
                            isFreeRoomsShown ? (
                                <LocalizedStrict id="service-center-meeting-rooms-group-hour-free-empty">
                                    <span>No available rooms</span>
                                </LocalizedStrict>
                            ) : (
                                <LocalizedStrict id="service-center-meeting-rooms-group-hour-empty">
                                    <span>No bookings</span>
                                </LocalizedStrict>
                            )
                        }
                    >
                        {isFreeRoomsShown
                            ? freeRooms.map((room) => (
                                  <MeetingRoomFreeRow
                                      key={room.id}
                                      room={room}
                                      trailing={hourLabel}
                                      searchRegex={searchRegex}
                                      onOpen={onRoomOpen}
                                  />
                              ))
                            : items.map((item) => (
                                  <MeetingRoomBookingRow
                                      key={`${item.booking.orderId}-${item.room.id}-${hour}`}
                                      item={item}
                                      lead={<SearchableText search={searchRegex}>{item.room.name}</SearchableText>}
                                      trailing={formatTimeWindow(dateTimeFactory, item.timeRange)}
                                      searchRegex={searchRegex}
                                      onOpen={onOpen}
                                  />
                              ))}
                    </MeetingRoomsGroup>
                )
            })}
        </>
    )
}
